import classNames from 'classnames';
import React from 'react';
import { type MobileHeroProps } from 'src/components/Hero/MobileHero';
import { useUserCurrency } from 'src/hooks/use-user-currency';

type ProgressTileProps = NonNullable<MobileHeroProps['homeProgressRingProps']>;
export type SidebarGamificationProps = Omit<MobileHeroProps, 'compact' | 'variant'>;

interface SidebarGamificationComponentProps extends SidebarGamificationProps {
  compact?: boolean;
}

const getProgressPercent = (currentValue: number, maxValue: number) => {
  if (maxValue <= 0) return 0;
  return Math.min(100, Math.max(0, (currentValue / maxValue) * 100));
};

const getNodeText = (node: React.ReactNode) => (typeof node === 'string' || typeof node === 'number' ? node : null);

const SidebarGamification: React.FC<SidebarGamificationComponentProps> = ({
  compact = false,
  showProgressRing = false,
  progressRingProps,
  showStats = false,
  stats = [],
  showRewardInfo = false,
  rewardInfo,
  homeProgressRingProps,
  rewardsProgressRingProps,
  rewardProgressRingProps,
}) => {
  const { formatCurrency, formatPoints } = useUserCurrency();
  const levelProgressProps = homeProgressRingProps || progressRingProps || rewardsProgressRingProps;

  const renderProgressTile = (title: string, tile: ProgressTileProps, barClassName: string, isMoney = false) => {
    const tileContent = (
      <>
        <div className="flex min-w-0 items-start justify-between gap-2">
          <span className="truncate text-[11px] font-bold uppercase leading-4 text-violet-700">{title}</span>
          <span className="inline-flex min-w-0 shrink-0 items-center gap-1 font-spaceGrotesk text-lg font-bold leading-5 text-slate-900">
            <span className="truncate">{tile.label}</span>
            {tile.subLabel && !getNodeText(tile.subLabel) && (
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">{tile.subLabel}</span>
            )}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-violet-100">
          <div
            className={classNames('h-full rounded-full', barClassName)}
            style={{ width: `${getProgressPercent(tile.currentValue, tile.maxValue)}%` }}
          />
        </div>
        <div className="mt-1.5 truncate text-[11px] font-semibold leading-4 text-slate-500">
          {isMoney
            ? `${formatPoints(tile.currentValue)} / ${formatPoints(tile.maxValue)}`
            : `${tile.currentValue.toLocaleString()} / ${tile.maxValue.toLocaleString()}`}
        </div>
      </>
    );
    const className =
      'w-full rounded-xl border border-violet-100 bg-white/90 px-3 py-2 text-left shadow-sm transition-all duration-200';

    return tile.onClick ? (
      <button
        key={title}
        type="button"
        className={`${className} hover:border-violet-200 hover:bg-violet-50/70`}
        onClick={tile.onClick}
      >
        {tileContent}
      </button>
    ) : (
      <div key={title} className={className}>
        {tileContent}
      </div>
    );
  };

  const renderCompactProgressTile = (title: string, tile: ProgressTileProps, barClassName: string) => {
    const subLabelText = getNodeText(tile.subLabel);
    const tileContent = (
      <>
        <span className="text-[9px] font-bold uppercase leading-3 text-violet-700">{title.slice(0, 3)}</span>
        <span className="mt-0.5 max-w-full truncate font-spaceGrotesk text-sm font-bold leading-4 text-slate-900">
          {tile.label}
        </span>
        {subLabelText ? (
          <span className="max-w-full truncate text-[9px] font-semibold leading-3 text-slate-500">{subLabelText}</span>
        ) : tile.subLabel ? (
          <span className="mt-0.5 inline-flex h-3.5 w-3.5 items-center justify-center text-slate-500">
            {tile.subLabel}
          </span>
        ) : null}
        <span className="mt-1 h-1 w-full overflow-hidden rounded-full bg-violet-100">
          <span
            className={classNames('block h-full rounded-full', barClassName)}
            style={{ width: `${getProgressPercent(tile.currentValue, tile.maxValue)}%` }}
          />
        </span>
      </>
    );
    const className =
      'flex h-[64px] w-14 flex-col items-center justify-center rounded-xl border border-violet-100 bg-white/90 px-1.5 text-center shadow-sm transition-all duration-200';

    return tile.onClick ? (
      <button
        key={title}
        type="button"
        title={title}
        aria-label={title}
        className={`${className} hover:border-violet-200 hover:bg-violet-50/70`}
        onClick={tile.onClick}
      >
        {tileContent}
      </button>
    ) : (
      <div key={title} title={title} className={className}>
        {tileContent}
      </div>
    );
  };

  const renderStatTile = (stat: NonNullable<SidebarGamificationProps['stats']>[number], index: number) => {
    const statContent = (
      <>
        {stat.label && (
          <span className="truncate text-[11px] font-bold uppercase leading-4 text-slate-500">{stat.label}</span>
        )}
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-bold leading-5 text-slate-900">
          {stat.icon && <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">{stat.icon}</span>}
          <span className="truncate">
            {stat.customLabel || (
              <>
                {stat.value}
                {stat.unit ? ` ${stat.unit}` : ''}
              </>
            )}
          </span>
        </span>
      </>
    );
    const className =
      'flex min-w-0 flex-col rounded-xl border border-violet-100 bg-white/90 px-3 py-2 text-left shadow-sm transition-all duration-200';

    return stat.onClick ? (
      <button
        key={index}
        type="button"
        className={`${className} hover:border-violet-200 hover:bg-violet-50/70`}
        onClick={stat.onClick}
      >
        {statContent}
      </button>
    ) : (
      <div key={index} className={className}>
        {statContent}
      </div>
    );
  };

  const renderCompactStatTile = (stat: NonNullable<SidebarGamificationProps['stats']>[number], index: number) => {
    const statContent = (
      <>
        {stat.icon && <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">{stat.icon}</span>}
        <span className="mt-0.5 max-w-full truncate text-[10px] font-bold leading-3 text-slate-900">
          {stat.customLabel || (
            <>
              {stat.value}
              {stat.unit ? ` ${stat.unit}` : ''}
            </>
          )}
        </span>
      </>
    );
    const className =
      'flex h-12 w-14 flex-col items-center justify-center rounded-xl border border-violet-100 bg-white/90 px-1.5 text-center shadow-sm transition-all duration-200';

    return stat.onClick ? (
      <button
        key={index}
        type="button"
        title={stat.label || String(stat.customLabel || stat.value)}
        className={`${className} hover:border-violet-200 hover:bg-violet-50/70`}
        onClick={stat.onClick}
      >
        {statContent}
      </button>
    ) : (
      <div key={index} title={stat.label || String(stat.customLabel || stat.value)} className={className}>
        {statContent}
      </div>
    );
  };

  const progressTiles = [
    showProgressRing && levelProgressProps
      ? { title: 'Level', props: levelProgressProps, barClassName: 'bg-violet-600', isMoney: false }
      : null,
    rewardProgressRingProps
      ? {
          title: 'Balance',
          props: rewardProgressRingProps,
          barClassName: 'bg-purple-900',
          isMoney: true,
        }
      : null,
  ].filter((tile): tile is { title: string; props: ProgressTileProps; barClassName: string; isMoney: boolean } =>
    Boolean(tile)
  );

  if (compact) {
    return (
      <div className="border-b border-violet-100 bg-violet-50/70 px-2 py-3 shadow-sm">
        <div className="flex flex-col items-center gap-2">
          {progressTiles.map((tile) => renderCompactProgressTile(tile.title, tile.props, tile.barClassName))}
          {showStats && stats.map(renderCompactStatTile)}
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-violet-100 px-4 py-4">
      <div className="overflow-hidden rounded-2xl border border-violet-100 bg-gradient-to-b from-white to-violet-50/80 p-3 shadow-sm ring-1 ring-violet-900/5">
        <div className="flex flex-col gap-2">
          {progressTiles.map((tile) => renderProgressTile(tile.title, tile.props, tile.barClassName, tile.isMoney))}
          {showStats && stats.map(renderStatTile)}
          {showRewardInfo && (
            <div
              className={classNames(
                'rounded-xl border border-violet-100 bg-white/90 px-3 py-2 text-sm font-bold text-slate-900 shadow-sm',
                rewardInfo?.onClick &&
                  'cursor-pointer transition-all duration-200 hover:border-violet-200 hover:bg-violet-50/70'
              )}
              onClick={rewardInfo?.onClick}
            >
              {rewardInfo ? `${formatCurrency(rewardInfo.amount)} ${rewardInfo.name}` : 'Select reward'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SidebarGamification;
